/**
 * Utility functions for patient-related operations
 */

/**
 * Format a date string to localized format
 * @param dateStr Date string to format
 * @returns Formatted date string
 */
export const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return 'N/A';
  
  try {
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
};

/**
 * Format phone number to make it more readable
 * @param phone Phone number to format
 * @returns Formatted phone number
 */
export const formatPhone = (phone: string | undefined): string => {
  if (!phone) return '';
  
  // Format Australian mobile numbers
  if (phone.length === 10 && phone.startsWith('04')) {
    return phone.replace(/^(\d{4})(\d{3})(\d{3})$/, '$1 $2 $3');
  }
  
  // Format landline numbers
  if (phone.length === 10 && !phone.startsWith('04')) {
    return phone.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2 $3');
  }
  
  // Return original if no format matches
  return phone;
};

/**
 * Calculate age from birth date
 * @param dateOfBirth Date of birth string
 * @returns Age in years
 */
export const calculateAge = (dateOfBirth: string | undefined): number => {
  if (!dateOfBirth) return 0;
  
  try {
    const birthDate = new Date(dateOfBirth);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    // Adjust age if birthday hasn't occurred yet this year
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  } catch (error) {
    console.error('Error calculating age:', error);
    return 0;
  }
};

/**
 * Format currency amount
 * @param amount Amount to format
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number | undefined): string => {
  if (amount === undefined) return '$0.00';
  
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2
  }).format(amount);
};

/**
 * Format a patient's full name
 * @param firstName - Patient's first name
 * @param lastName - Patient's last name
 * @returns Formatted full name
 */
export const formatFullName = (firstName: string, lastName: string): string => {
  if (!firstName && !lastName) return '';
  return `${firstName || ''} ${lastName || ''}`.trim();
};

/**
 * Generate patient initials from first and last name
 * @param firstName - Patient's first name
 * @param lastName - Patient's last name
 * @returns Patient's initials
 */
export const getInitials = (firstName: string, lastName: string): string => {
  if (!firstName && !lastName) return '';
  
  let initials = '';
  if (firstName) initials += firstName.charAt(0);
  if (lastName) initials += lastName.charAt(0);
  
  return initials.toUpperCase();
};

/**
 * Format an address into a single string
 * @param address - Address object with street, city, state, postalCode
 * @returns Formatted address string
 */
export const formatAddress = (address: { street?: string; city?: string; state?: string; postalCode?: string }): string => {
  if (!address) return '';
  
  const { street, city, state, postalCode } = address;
  let parts: string[] = [];
  
  if (street) parts.push(street);
  if (city || state || postalCode) {
    let cityStatePostal = '';
    if (city) cityStatePostal += city;
    if (state) cityStatePostal += cityStatePostal ? `, ${state}` : state;
    if (postalCode) cityStatePostal += cityStatePostal ? ` ${postalCode}` : postalCode;
    parts.push(cityStatePostal);
  }
  
  return parts.join(', ');
};

/**
 * Get the appropriate status color
 */
export const getStatusColor = (status: string): { text: string, bg: string } => {
  switch (status?.toLowerCase()) {
    case 'complete':
    case 'completed':
    case 'done':
      return { text: 'text-green-700', bg: 'bg-green-100' };
    case 'in_progress':
    case 'inprogress':
    case 'in progress':
      return { text: 'text-blue-700', bg: 'bg-blue-100' };
    case 'pending':
    case 'waiting':
      return { text: 'text-yellow-700', bg: 'bg-yellow-100' };
    case 'overdue':
    case 'expired':
      return { text: 'text-red-700', bg: 'bg-red-100' };
    default:
      return { text: 'text-gray-700', bg: 'bg-gray-100' };
  }
};

/**
 * Get status icon for a given status
 */
export const getStatusIcon = (status: string): null => {
  // This would be implemented with actual icon components when moved to a TSX file
  return null;
};

/**
 * Capitalize first letter of a string
 */
export const capitalize = (str: string | undefined): string => {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1);
};